import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';
import { nacmeLogo } from './assets/images';

const BACKEND_URL = API_URL;

// Component for collapsible requirement dropdowns
const RequirementDropdown = ({ label, items, companyIndex, fieldName, expandedItems, setExpandedItems }) => {
  const dropdownKey = `${companyIndex}-${fieldName}`;
  const isExpanded = expandedItems.has(dropdownKey);
  
  const toggleDropdown = (e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(dropdownKey)) {
      newExpanded.delete(dropdownKey);
    } else {
      newExpanded.add(dropdownKey);
    }
    setExpandedItems(newExpanded);
  };

  if (!items || items.length === 0) {
    return (
      <div>
        <p className="font-medium text-gray-700 text-sm">{label}:</p>
        <p className="text-gray-600 text-sm">Not specified</p>
      </div>
    );
  }

  const displayText = items.length === 1 ? items[0] : `${items.length} items`;
  const shouldShowDropdown = items.length > 1;

  return (
    <div>
      <p className="font-medium text-gray-700 text-sm">{label}:</p>
      {shouldShowDropdown ? (
        <div>
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
          >
            <span>{displayText}</span>
            <svg 
              className={`w-3 h-3 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isExpanded && (
            <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
              <ul className="list-disc list-inside space-y-1">
                {items.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-600 text-sm">{displayText}</p>
      )}
    </div>
  );
};

const CompanyInterestSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const magicLink = searchParams.get('ml');

  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [applicantId, setApplicantId] = useState(null);
  const [existingSelections, setExistingSelections] = useState(new Set());
  const [loadingExisting, setLoadingExisting] = useState(true);

  useEffect(() => {
    // Check if student just created account (localStorage will be fresh)
    const studentInfo = localStorage.getItem('studentInfo');
    const authToken = localStorage.getItem('studentAuthToken');

    if (studentInfo && authToken) {
      // Student is authenticated - use localStorage (handles just-created accounts)
      const student = JSON.parse(studentInfo);
      setApplicantId(student.id);
      fetchMatchingCompanies(student.id);
    } else if (token || magicLink) {
      // No localStorage auth - validate URL token
      fetchApplicantId();
    } else {
      // No authentication available
      setError('No authentication provided');
      setLoading(false);
    }
  }, [token, magicLink]);

  const fetchApplicantId = async () => {
    try {
      // First, get the applicant ID from the token
      let response;
      if (magicLink) {
        response = await axios.get(`${BACKEND_URL}/api/validate-magic-link?ml=${magicLink}`);
      } else {
        response = await axios.get(`${BACKEND_URL}/api/validate-update-token?token=${token}`);
      }

      const applicantId = response.data.applicant_id;
      setApplicantId(applicantId);

      // Then fetch their matching companies
      await fetchMatchingCompanies(applicantId);
    } catch (err) {
      console.error('Error validating token:', err);
      setError('Invalid or expired token');
      setLoading(false);
    }
  };

  const fetchMatchingCompanies = async (applicantId) => {
    try {
      // Fetch matching companies
      const matchesResponse = await axios.get(`${BACKEND_URL}/api/applicant-company-matches/${applicantId}`);
      
      if (matchesResponse.data.total_matches === 0) {
        // No matches, redirect directly to registration form
        navigate(`/application-form?token=${token}`);
        return;
      }
      
      setMatchingCompanies(matchesResponse.data.matching_companies);
      
      // Fetch existing interests
      try {
        const interestsResponse = await axios.get(`${BACKEND_URL}/api/applicant-company-interests/${applicantId}`);
        const existingInterests = new Set(
          interestsResponse.data.interests
            .filter(interest => interest.is_interested === true)
            .map(interest => interest.recruiter_id)
        );
        setExistingSelections(existingInterests);
      } catch (err) {
        console.error('Error fetching existing interests:', err);
        // Continue even if we can't fetch existing interests
      }
      
      setLoadingExisting(false);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching matching companies:', err);
      setError('Failed to load matching companies');
      setLoading(false);
    }
  };

  const toggleCompanySelection = (recruiterId) => {
    // Don't allow toggling if already selected
    if (existingSelections.has(recruiterId)) {
      return;
    }
    
    const newSelected = new Set(selectedCompanies);
    if (newSelected.has(recruiterId)) {
      newSelected.delete(recruiterId);
    } else {
      newSelected.add(recruiterId);
    }
    setSelectedCompanies(newSelected);
  };

  const selectAllCompanies = () => {
    // Only select companies that haven't been previously selected
    const availableRecruiterIds = matchingCompanies
      .filter(company => !existingSelections.has(company.recruiter_id))
      .map(company => company.recruiter_id);
    setSelectedCompanies(new Set(availableRecruiterIds));
  };

  const clearAllSelections = () => {
    setSelectedCompanies(new Set());
  };

  const toggleDescriptionExpansion = (recruiterId, e) => {
    e.stopPropagation(); // Prevent card selection when clicking description
    const newExpanded = new Set(expandedDescriptions);
    if (newExpanded.has(recruiterId)) {
      newExpanded.delete(recruiterId);
    } else {
      newExpanded.add(recruiterId);
    }
    setExpandedDescriptions(newExpanded);
  };

  const submitInterests = async () => {
    if (!applicantId) {
      setError('Applicant ID not found');
      return;
    }

    // Filter out any already selected companies (just in case)
    const newSelections = Array.from(selectedCompanies).filter(
      recruiterId => !existingSelections.has(recruiterId)
    );

    // If no new selections, just navigate
    if (newSelections.length === 0) {
      navigate(`/application-form?token=${token}`);
      return;
    }

    setSubmitting(true);
    try {
      devLog('Starting request...');
      const response = await axios.post(`${BACKEND_URL}/api/applicant-company-interests`, {
        applicant_id: applicantId,
        interested_recruiter_ids: newSelections
      });
      devLog('Request completed successfully:', response.status);

      // Redirect to registration form with token
      devLog('Navigating to application form...');
      navigate(`/application-form?token=${token}`);
      setSubmitting(false); // Reset submitting state after navigation
    } catch (err) {
      console.error('Error submitting interests:', err);
      setError('Failed to save your interests. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your company matches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src={nacmeLogo}
            alt="NACME Logo"
            className="h-16 w-auto mx-auto mb-4 drop-shadow"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Company Interest Selection
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Your background matches these companies. Please select the companies that you're interested in interning with next summer.
          </p>
        </div>

        {/* Selection Controls */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              <button
                onClick={selectAllCompanies}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Select All ({matchingCompanies.length})
              </button>
              <button
                onClick={clearAllSelections}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Clear Selection
              </button>
              <span className="text-gray-600">
                {selectedCompanies.size} new selections • {existingSelections.size} previously selected • {matchingCompanies.length} total matches
              </span>
            </div>
            <button
              onClick={submitInterests}
              disabled={submitting}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Continue to Application'
              )}
            </button>
          </div>
        </div>

        {/* Company Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {matchingCompanies.map((company, index) => (
            <div
              key={company.recruiter_id}
              className={`bg-white rounded-lg shadow-lg border-2 transition-all relative group overflow-hidden ${
                existingSelections.has(company.recruiter_id)
                  ? 'border-green-500 bg-green-50 pb-10'
                  : selectedCompanies.has(company.recruiter_id)
                  ? 'border-blue-500 bg-blue-50 cursor-pointer p-6'
                  : 'border-gray-200 hover:border-gray-300 cursor-pointer p-6'
              }`}
              onClick={() => !existingSelections.has(company.recruiter_id) && toggleCompanySelection(company.recruiter_id)}
            >
              {/* Card Content Wrapper */}
              <div className={existingSelections.has(company.recruiter_id) ? 'p-6' : ''}>
              {/* Company Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  {/* Company Logo */}
                  {company.logo && (
                    <div className="mb-3 flex justify-center">
                      <img
                        src={company.logo.startsWith('/public/') ? company.logo.replace('/public/', '/') : company.logo}
                        alt={`${company.organization} logo`}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          devLog(`Failed to load logo: ${e.target.src}`);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {company.organization}
                  </h3>
                  <div className="flex items-center gap-2">
                    {existingSelections.has(company.recruiter_id) ? (
                      <>
                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-green-700">
                          I am interested in this company
                        </span>
                      </>
                    ) : (
                      <>
                        <input
                          type="checkbox"
                          checked={selectedCompanies.has(company.recruiter_id)}
                          onChange={() => toggleCompanySelection(company.recruiter_id)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm text-gray-600">
                          {selectedCompanies.has(company.recruiter_id) ? 'Selected' : 'Click to select'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {company.approved && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    APPROVED
                  </span>
                )}
              </div>

              {/* Basic Company Info */}
              <div className="space-y-3 mb-4">
                <div>
                  <p className="font-medium text-gray-700 text-sm">Minimum GPA:</p>
                  <p className="text-gray-600 text-sm">{company.minimum_gpa}</p>
                </div>
                
                {/* Internship Description */}
                {company.internship_description && (
                  <div>
                    <button
                      onClick={(e) => toggleDescriptionExpansion(company.recruiter_id, e)}
                      className="flex items-center space-x-1 font-bold text-blue-600 text-sm hover:text-blue-700 transition-colors"
                    >
                      <span>Internship Description</span>
                      <svg 
                        className={`w-3 h-3 transform transition-transform ${
                          expandedDescriptions.has(company.recruiter_id) ? 'rotate-180' : ''
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedDescriptions.has(company.recruiter_id) && (
                      <div className="mt-2 p-3 bg-gray-50 rounded border text-sm text-gray-700">
                        {company.internship_description.split('\n').map((paragraph, idx) => (
                          <p key={idx} className={idx > 0 ? 'mt-3' : ''}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div>
                  <p className="font-medium text-gray-700 text-sm">Locations:</p>
                  <p className="text-gray-600 text-sm">{company.company_locations}</p>
                </div>
                
                {/* External Application Link */}
                {company.external_application_link && company.external_application_link !== 'null' && (
                  <div>
                    <p className="font-medium text-gray-700 text-sm">Application Link:</p>
                    <a
                      href={company.external_application_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm hover:text-blue-700 underline break-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Apply Online
                    </a>
                  </div>
                )}
              </div>

              {/* Requirements */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <RequirementDropdown 
                  label="Eligible Majors"
                  items={company.eligible_majors}
                  companyIndex={index}
                  fieldName="majors"
                  expandedItems={expandedItems}
                  setExpandedItems={setExpandedItems}
                />
                <RequirementDropdown 
                  label="Preferred Institutions"
                  items={company.preferred_institutions}
                  companyIndex={index}
                  fieldName="institutions"
                  expandedItems={expandedItems}
                  setExpandedItems={setExpandedItems}
                />
                <div>
                  <p className="font-medium text-gray-700 text-sm">Preferred Class Years:</p>
                  <p className="text-gray-600 text-sm">
                    {company.preferred_class_years ? company.preferred_class_years.join(', ') : 'Not specified'}
                  </p>
                </div>
              </div>
              </div>
              
              {/* Previously Selected Indicator */}
              {existingSelections.has(company.recruiter_id) && (
                <div className="absolute bottom-0 left-0 right-0 bg-green-600 text-white px-4 py-2 text-center text-sm font-medium">
                  I am interested in this company
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Action */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            You can always change your interests later in your application.
          </p>
          <button
            onClick={submitInterests}
            disabled={submitting}
            className="px-8 py-3 bg-green-600 text-white text-lg rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting Your Interests...
              </>
            ) : (
              selectedCompanies.size > 0 ? 
                `Continue with ${selectedCompanies.size} New Selections` : 
                'Continue to Application'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyInterestSelection;